import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Route as RouteIcon,
  DirectionsCar as VehicleIcon,
  Person as DriverIcon,
  Timeline as TimelineIcon,
  AddLocation as AddLocationIcon,
  RemoveCircle as RemoveCircleIcon,
  PlayArrow as StartIcon,
  Cancel as CancelIcon,
  CheckCircle as CompleteIcon,
  PersonAdd as PersonAddIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  Close as CloseIcon,
  Schedule as ClockIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

const TripManagement = () => {
  const [trips, setTrips] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [hrDrivers, setHrDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTrip, setEditingTrip] = useState(null);
  const [manualDriverEntry, setManualDriverEntry] = useState(false);
  const [newDriver, setNewDriver] = useState({
    name: '',
    phone: '',
    license_number: '',
    license_expiry: ''
  });

  // View dialog state
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewingTrip, setViewingTrip] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [formData, setFormData] = useState({
    vehicle: '',
    driver: '',
    driver_type: 'dispatch',
    start_date: '',
    end_date: '',
    pickup_location: '',
    delivery_location: '',
    notes: '',
    route_points: []
  });

  const [routePointForm, setRoutePointForm] = useState({
    location_name: '',
    mileage_reading: ''
  });

  const TRIP_STATUSES = [
    { value: 'planned', label: 'Planned' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [tripsRes, vehiclesRes, driversRes, hrDriversRes] = await Promise.all([
        api.get('/dispatch/trips/'),
        api.get('/dispatch/vehicles/'),
        api.get('/dispatch/drivers/'),
        api.get('/hr/employees/?designation=Driver')
      ]);

      setTrips(Array.isArray(tripsRes.data) ? tripsRes.data : tripsRes.data?.results || []);
      setVehicles(Array.isArray(vehiclesRes.data) ? vehiclesRes.data : vehiclesRes.data?.results || []);

      const dispatchDrivers = Array.isArray(driversRes.data) ? driversRes.data : driversRes.data?.results || [];
      setDrivers(dispatchDrivers);

      const hrDriversData = Array.isArray(hrDriversRes.data) ? hrDriversRes.data : hrDriversRes.data?.results || [];
      setHrDrivers(hrDriversData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleHrDriverSelect = async (hrDriverId) => {
    const selectedHrDriver = hrDrivers.find(d => d.id === parseInt(hrDriverId));
    if (!selectedHrDriver) return;

    try {
      const existingDriver = drivers.find(d =>
        d.name === selectedHrDriver.full_name ||
        d.name === selectedHrDriver.employee_id
      );

      if (existingDriver) {
        setFormData(prev => ({
          ...prev,
          driver: existingDriver.id,
          driver_type: 'dispatch'
        }));
        toast.info('Using existing driver record');
        return;
      }

      const response = await api.post('/dispatch/drivers/', {
        name: selectedHrDriver.full_name || selectedHrDriver.employee_id,
        phone: selectedHrDriver.phone || 'N/A',
        license_number: `HR-${selectedHrDriver.employee_id}`,
        license_expiry: null,
        is_active: true
      });

      const driversRes = await api.get('/dispatch/drivers/');
      setDrivers(Array.isArray(driversRes.data) ? driversRes.data : driversRes.data?.results || []);

      setFormData(prev => ({
        ...prev,
        driver: response.data.id,
        driver_type: 'dispatch'
      }));

      toast.success('HR Driver added to dispatch records');
    } catch (error) {
      console.error('Error creating dispatch driver from HR:', error);
      toast.error('Failed to add HR driver to dispatch');
    }
  };

  const handleOpenDialog = (trip = null) => {
    if (trip) {
      setEditingTrip(trip);
      setFormData({
        vehicle: trip.vehicle || '',
        driver: trip.driver || '',
        driver_type: 'dispatch',
        start_date: trip.start_date || '',
        end_date: trip.end_date || '',
        pickup_location: trip.pickup_location || '',
        delivery_location: trip.delivery_location || '',
        notes: trip.notes || '',
        route_points: trip.route_points || []
      });
      setManualDriverEntry(false);
    } else {
      setEditingTrip(null);
      setFormData({
        vehicle: '',
        driver: '',
        driver_type: 'dispatch',
        start_date: '',
        end_date: '',
        pickup_location: '',
        delivery_location: '',
        notes: '',
        route_points: []
      });
      setRoutePointForm({ location_name: '', mileage_reading: '' });
      setManualDriverEntry(false);
      setNewDriver({
        name: '',
        phone: '',
        license_number: '',
        license_expiry: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTrip(null);
    setManualDriverEntry(false);
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDriverChange = (event) => {
    const value = event.target.value;
    if (typeof value === 'string' && value.startsWith('hr-')) {
      const hrId = parseInt(value.replace('hr-', ''));
      setFormData(prev => ({
        ...prev,
        driver: value,
        driver_type: 'hr'
      }));
      handleHrDriverSelect(hrId);
    } else {
      setFormData(prev => ({
        ...prev,
        driver: value,
        driver_type: 'dispatch'
      }));
    }
  };

  const handleRoutePointChange = (field) => (event) => {
    setRoutePointForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const addRoutePoint = () => {
    if (!routePointForm.location_name || !routePointForm.mileage_reading) {
      toast.error('Please enter location name and mileage reading');
      return;
    }

    setFormData(prev => ({
      ...prev,
      route_points: [
        ...prev.route_points,
        {
          location_name: routePointForm.location_name,
          mileage_reading: parseFloat(routePointForm.mileage_reading)
        }
      ]
    }));

    setRoutePointForm({ location_name: '', mileage_reading: '' });
    toast.success('Route point added');
  };

  const removeRoutePoint = (index) => {
    setFormData(prev => ({
      ...prev,
      route_points: prev.route_points.filter((_, i) => i !== index)
    }));
  };

  const calculateTotalMileage = () => {
    const points = formData.route_points;
    if (points.length >= 2) {
      const first = points[0];
      const last = points[points.length - 1];
      return last.mileage_reading - first.mileage_reading;
    }
    return 0;
  };

  const handleManualDriverToggle = () => {
    setManualDriverEntry(!manualDriverEntry);
    if (!manualDriverEntry) {
      setFormData(prev => ({ ...prev, driver: '' }));
    }
  };

  const handleNewDriverChange = (field) => (event) => {
    setNewDriver(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleAddNewDriver = async () => {
    try {
      if (!newDriver.name) {
        toast.error('Driver name is required');
        return;
      }
      if (!newDriver.phone) {
        toast.error('Driver phone is required');
        return;
      }

      const response = await api.post('/dispatch/drivers/', {
        name: newDriver.name,
        phone: newDriver.phone,
        license_number: newDriver.license_number || `LIC-${Date.now()}`,
        license_expiry: newDriver.license_expiry || null,
        is_active: true
      });

      toast.success('Driver added successfully');

      const driversRes = await api.get('/dispatch/drivers/');
      setDrivers(Array.isArray(driversRes.data) ? driversRes.data : driversRes.data?.results || []);

      setFormData(prev => ({
        ...prev,
        driver: response.data.id,
        driver_type: 'dispatch'
      }));

      setManualDriverEntry(false);
      setNewDriver({
        name: '',
        phone: '',
        license_number: '',
        license_expiry: ''
      });
    } catch (error) {
      console.error('Error adding driver:', error);
      toast.error('Failed to add driver');
    }
  };

  const handleSubmit = async () => {
    try {
      if (!formData.vehicle) return toast.error('Please select a vehicle');
      if (!formData.driver) return toast.error('Please select a driver');
      if (!formData.start_date) return toast.error('Please select start date and time');
      if (!formData.pickup_location) return toast.error('Please enter pickup location');
      if (!formData.delivery_location) return toast.error('Please enter delivery location');

      let driverId = formData.driver;
      if (typeof driverId === 'string' && driverId.startsWith('hr-')) {
        toast.error('Please wait for HR driver to be added');
        return;
      }

      const submitData = {
        vehicle: parseInt(formData.vehicle),
        driver: parseInt(driverId),
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        pickup_location: formData.pickup_location,
        delivery_location: formData.delivery_location,
        notes: formData.notes || '',
        route_points: formData.route_points.map((point, index) => ({
          sequence: index + 1,
          location_name: point.location_name,
          mileage_reading: point.mileage_reading
        }))
      };

      if (editingTrip) {
        await api.put(`/dispatch/trips/${editingTrip.id}/`, submitData);
        toast.success('Trip updated successfully');
      } else {
        await api.post('/dispatch/trips/', submitData);
        toast.success('Trip created successfully');
      }

      handleCloseDialog();
      fetchData();
    } catch (error) {
      console.error('Error saving trip:', error);
      if (error.response) {
        const errorMsg = typeof error.response.data === 'object'
          ? JSON.stringify(error.response.data)
          : error.response.data;
        toast.error(`Failed to save trip: ${errorMsg}`);
      } else {
        toast.error('Failed to save trip');
      }
    }
  };

  const updateTripStatus = async (id, status) => {
    try {
      await api.patch(`/dispatch/trips/${id}/`, { status });
      toast.success(`Trip status updated to ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update trip status');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this trip?')) {
      try {
        await api.delete(`/dispatch/trips/${id}/`);
        toast.success('Trip deleted successfully');
        fetchData();
      } catch (error) {
        toast.error('Failed to delete trip');
      }
    }
  };

  // ---------- View Details ----------

  const handleViewTrip = async (trip) => {
    setViewingTrip(trip);
    setOpenViewDialog(true);

    // Fetch fresh data (including route_points) from the detail endpoint
    setViewLoading(true);
    try {
      const res = await api.get(`/dispatch/trips/${trip.id}/`);
      setViewingTrip(res.data);
    } catch (err) {
      console.error('Error fetching trip details:', err);
      // Keep the row data we already have
    } finally {
      setViewLoading(false);
    }
  };

  const handleCloseView = () => {
    setOpenViewDialog(false);
    setViewingTrip(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      'planned': 'info',
      'in_progress': 'warning',
      'completed': 'success',
      'cancelled': 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'planned': 'Planned',
      'in_progress': 'In Progress',
      'completed': 'Completed',
      'cancelled': 'Cancelled'
    };
    return labels[status] || status;
  };

  const getTripDuration = (trip) => {
    if (!trip?.start_date || !trip?.end_date) return 'N/A';
    const start = new Date(trip.start_date);
    const end = new Date(trip.end_date);
    const ms = end - start;
    if (ms < 0) return 'N/A';
    const totalMinutes = Math.floor(ms / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;
    const parts = [];
    if (days) parts.push(`${days}d`);
    if (hours) parts.push(`${hours}h`);
    if (minutes || parts.length === 0) parts.push(`${minutes}m`);
    return parts.join(' ');
  };

  const fmtDateTime = (value) => {
    if (!value) return 'N/A';
    try { return format(new Date(value), 'dd/MM/yyyy HH:mm'); } catch { return 'N/A'; }
  };

  const fmtDate = (value) => {
    if (!value) return 'N/A';
    try { return format(new Date(value), 'dd/MM/yyyy'); } catch { return 'N/A'; }
  };

  const printTripDetails = (trip) => {
    if (!trip) return;
    const win = window.open('', '_blank', 'width=800,height=900');
    const rp = (trip.route_points || []).slice().sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

    const routeRows = rp.length === 0
      ? '<tr><td colspan="5" style="text-align:center;color:#666;">No route points recorded</td></tr>'
      : rp.map((p, i) => {
          const prev = i > 0 ? rp[i - 1] : null;
          const delta = prev ? (parseFloat(p.mileage_reading) - parseFloat(prev.mileage_reading)) : null;
          return `
            <tr>
              <td>${i + 1}</td>
              <td>${p.location_name || ''}</td>
              <td style="text-align:right;">${parseFloat(p.mileage_reading || 0).toFixed(2)} km</td>
              <td style="text-align:right;">${delta !== null ? delta.toFixed(2) + ' km' : '—'}</td>
              <td>${p.notes || ''}</td>
            </tr>
          `;
        }).join('');

    win.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Trip ${trip.trip_number || ''}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; color: #222; }
          .header { text-align: center; border-bottom: 2px solid #2E7D32; padding-bottom: 15px; margin-bottom: 20px; }
          .header h1 { color: #2E7D32; margin: 0; font-size: 22px; }
          .header p { margin: 4px 0; color: #666; font-size: 13px; }
          h2 { font-size: 16px; color: #1976D2; margin-top: 24px; margin-bottom: 8px; border-bottom: 1px solid #eee; padding-bottom: 4px; }
          table { width: 100%; border-collapse: collapse; margin-top: 8px; }
          table.detail td { padding: 6px 4px; border-bottom: 1px solid #eee; font-size: 13px; }
          table.detail td.label { font-weight: bold; width: 180px; color: #444; }
          table.route { border: 1px solid #ddd; }
          table.route th { background: #2E7D32; color: white; padding: 8px; text-align: left; font-size: 12px; }
          table.route td { padding: 8px; border-bottom: 1px solid #eee; font-size: 12px; }
          .chip { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; background: #e8f5e9; color: #2E7D32; }
          .footer { margin-top: 30px; text-align: center; border-top: 1px solid #ddd; padding-top: 15px; color: #666; font-size: 11px; }
          .no-print { text-align: center; margin-top: 20px; }
          @media print { .no-print { display: none; } body { margin: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Sri Shakthi Traders</h1>
          <p>Cashew Shell Oil Extraction Unit</p>
          <p>Trip Sheet</p>
        </div>

        <h2>Trip Information</h2>
        <table class="detail">
          <tr><td class="label">Trip Number:</td><td><strong>${trip.trip_number || 'N/A'}</strong></td></tr>
          <tr><td class="label">Status:</td><td><span class="chip">${getStatusLabel(trip.status)}</span></td></tr>
          <tr><td class="label">Start:</td><td>${fmtDateTime(trip.start_date)}</td></tr>
          <tr><td class="label">End:</td><td>${fmtDateTime(trip.end_date)}</td></tr>
          <tr><td class="label">Duration:</td><td>${getTripDuration(trip)}</td></tr>
          <tr><td class="label">Total Mileage:</td><td>${trip.total_mileage != null ? parseFloat(trip.total_mileage).toFixed(2) + ' km' : 'N/A'}</td></tr>
        </table>

        <h2>Vehicle &amp; Driver</h2>
        <table class="detail">
          <tr><td class="label">Vehicle Number:</td><td>${trip.vehicle_number || 'N/A'}</td></tr>
          <tr><td class="label">Vehicle Type:</td><td>${trip.vehicle_type || 'N/A'}</td></tr>
          <tr><td class="label">Driver Name:</td><td>${trip.driver_name || 'N/A'}</td></tr>
          <tr><td class="label">Driver Phone:</td><td>${trip.driver_phone || 'N/A'}</td></tr>
        </table>

        <h2>Route</h2>
        <table class="detail">
          <tr><td class="label">Pickup Location:</td><td>${trip.pickup_location || 'N/A'}</td></tr>
          <tr><td class="label">Delivery Location:</td><td>${trip.delivery_location || 'N/A'}</td></tr>
          <tr><td class="label">Notes:</td><td>${trip.notes || 'N/A'}</td></tr>
        </table>

        <h2>Route Points</h2>
        <table class="route">
          <thead>
            <tr><th>#</th><th>Location</th><th style="text-align:right;">Odometer</th><th style="text-align:right;">Distance</th><th>Notes</th></tr>
          </thead>
          <tbody>${routeRows}</tbody>
        </table>

        <h2>Record Info</h2>
        <table class="detail">
          <tr><td class="label">Created At:</td><td>${fmtDateTime(trip.created_at)}</td></tr>
          <tr><td class="label">Last Updated:</td><td>${fmtDateTime(trip.updated_at)}</td></tr>
        </table>

        <div class="footer">
          <p>This is a system generated trip sheet. No signature required.</p>
          <p>Printed on: ${new Date().toLocaleString('en-IN')}</p>
        </div>

        <div class="no-print">
          <button onclick="window.print()" style="padding:10px 30px;background:#2E7D32;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:15px;">Print / Save as PDF</button>
          <button onclick="window.close()" style="padding:10px 30px;background:#666;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:15px;margin-left:10px;">Close</button>
        </div>
      </body>
      </html>
    `);
    win.document.close();
  };

  const allDrivers = [...drivers, ...hrDrivers];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Trip Management
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Manage company vehicle trips and routes
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchData} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Create Trip
            </Button>
          </Box>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip
            label={`Dispatch Drivers: ${drivers.length}`}
            color="primary"
            size="small"
            variant="outlined"
          />
          <Chip
            label={`HR Drivers: ${hrDrivers.length}`}
            color="secondary"
            size="small"
            variant="outlined"
          />
          <Chip
            label={`Total Drivers: ${allDrivers.length}`}
            color="success"
            size="small"
          />
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Trip #</TableCell>
                  <TableCell>Vehicle</TableCell>
                  <TableCell>Driver</TableCell>
                  <TableCell>Pickup → Delivery</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Total Mileage</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trips.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body1" color="textSecondary">
                        No trips found. Click "Create Trip" to add one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  trips.map((trip) => {
                    const isHrDriver = hrDrivers.some(d => d.id === trip.driver?.id);
                    return (
                      <TableRow key={trip.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {trip.trip_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={<VehicleIcon />}
                            label={trip.vehicle_number || 'N/A'}
                            size="small"
                            variant="outlined"
                          />
                          {trip.vehicle_type && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              {trip.vehicle_type}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={<DriverIcon />}
                            label={trip.driver_name || 'N/A'}
                            size="small"
                            variant="outlined"
                            color={isHrDriver ? 'secondary' : 'primary'}
                          />
                          {isHrDriver && (
                            <Typography variant="caption" color="secondary" display="block">
                              HR Driver
                            </Typography>
                          )}
                          {trip.driver_phone && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              {trip.driver_phone}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" display="block">
                            <strong>From:</strong> {trip.pickup_location}
                          </Typography>
                          <Typography variant="caption" display="block">
                            <strong>To:</strong> {trip.delivery_location}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {trip.start_date ? format(new Date(trip.start_date), 'dd/MM/yyyy HH:mm') : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(trip.status)}
                            color={getStatusColor(trip.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          {trip.total_mileage !== undefined && trip.total_mileage !== null
                            ? `${parseFloat(trip.total_mileage).toFixed(2)} km`
                            : 'N/A'}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleViewTrip(trip)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenDialog(trip)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          {trip.status === 'planned' && (
                            <Tooltip title="Start Trip">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => updateTripStatus(trip.id, 'in_progress')}
                              >
                                <StartIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {trip.status === 'in_progress' && (
                            <Tooltip title="Complete Trip">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => updateTripStatus(trip.id, 'completed')}
                              >
                                <CompleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {(trip.status === 'planned' || trip.status === 'in_progress') && (
                            <Tooltip title="Cancel Trip">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => updateTripStatus(trip.id, 'cancelled')}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(trip.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Create/Edit Trip Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          {editingTrip ? 'Edit Trip' : 'Create New Trip'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Vehicle</InputLabel>
                <Select
                  value={formData.vehicle}
                  onChange={handleChange('vehicle')}
                  label="Vehicle"
                >
                  <MenuItem value="">Select Vehicle</MenuItem>
                  {vehicles.map((v) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.vehicle_number} - {v.vehicle_type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              {!manualDriverEntry ? (
                <FormControl fullWidth>
                  <InputLabel>Driver</InputLabel>
                  <Select
                    value={formData.driver}
                    onChange={handleDriverChange}
                    label="Driver"
                  >
                    <MenuItem value="">Select Driver</MenuItem>

                    {hrDrivers.length > 0 && (
                      <MenuItem disabled>
                        <em>── HR Drivers (Auto-add to Dispatch) ──</em>
                      </MenuItem>
                    )}
                    {hrDrivers.map((d) => (
                      <MenuItem key={`hr-${d.id}`} value={`hr-${d.id}`}>
                        {d.full_name || d.employee_id} - {d.designation}
                        {d.phone ? ` (${d.phone})` : ''}
                        <Chip
                          label="HR"
                          size="small"
                          color="secondary"
                          sx={{ ml: 1 }}
                        />
                      </MenuItem>
                    ))}

                    {drivers.length > 0 && (
                      <MenuItem disabled>
                        <em>── Dispatch Drivers ──</em>
                      </MenuItem>
                    )}
                    {drivers.map((d) => (
                      <MenuItem key={`dispatch-${d.id}`} value={d.id}>
                        {d.name} ({d.phone})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <Box>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Driver Name"
                        value={newDriver.name}
                        onChange={handleNewDriverChange('name')}
                        size="small"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Phone"
                        value={newDriver.phone}
                        onChange={handleNewDriverChange('phone')}
                        size="small"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="License Number"
                        value={newDriver.license_number}
                        onChange={handleNewDriverChange('license_number')}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="License Expiry"
                        type="date"
                        value={newDriver.license_expiry}
                        onChange={handleNewDriverChange('license_expiry')}
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box display="flex" gap={1}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleAddNewDriver}
                          startIcon={<PersonAddIcon />}
                        >
                          Add Driver
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleManualDriverToggle}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
              {!manualDriverEntry && (
                <Button
                  variant="text"
                  size="small"
                  onClick={handleManualDriverToggle}
                  startIcon={<PersonAddIcon />}
                  sx={{ mt: 1 }}
                >
                  Driver not in list? Add manually
                </Button>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date & Time"
                type="datetime-local"
                value={formData.start_date}
                onChange={handleChange('start_date')}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date & Time"
                type="datetime-local"
                value={formData.end_date}
                onChange={handleChange('end_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Pickup Location"
                value={formData.pickup_location}
                onChange={handleChange('pickup_location')}
                required
                placeholder="Enter pickup location"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Delivery Location"
                value={formData.delivery_location}
                onChange={handleChange('delivery_location')}
                required
                placeholder="Enter delivery location"
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle2" color="primary">
                  <RouteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Route Points
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {formData.route_points.length} point(s) added
                </Typography>
              </Box>
              <Typography variant="caption" color="textSecondary">
                Add route points in sequence to track the complete journey
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  label="Location Name"
                  value={routePointForm.location_name}
                  onChange={handleRoutePointChange('location_name')}
                  placeholder="Enter location name"
                  size="small"
                />
                <TextField
                  fullWidth
                  label="Mileage (km)"
                  type="number"
                  value={routePointForm.mileage_reading}
                  onChange={handleRoutePointChange('mileage_reading')}
                  placeholder="Odometer reading"
                  size="small"
                  sx={{ maxWidth: 150 }}
                />
                <Button
                  variant="outlined"
                  onClick={addRoutePoint}
                  startIcon={<AddLocationIcon />}
                >
                  Add
                </Button>
              </Box>
            </Grid>

            {formData.route_points.length > 0 && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 1, maxHeight: 200, overflow: 'auto' }}>
                  <List dense>
                    {formData.route_points.map((point, index) => (
                      <ListItem
                        key={index}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            size="small"
                            color="error"
                            onClick={() => removeRoutePoint(index)}
                          >
                            <RemoveCircleIcon />
                          </IconButton>
                        }
                      >
                        <ListItemIcon>
                          <LocationIcon color={index === 0 ? 'primary' : index === formData.route_points.length - 1 ? 'success' : 'action'} />
                        </ListItemIcon>
                        <ListItemText
                          primary={point.location_name}
                          secondary={`Mileage: ${point.mileage_reading} km`}
                        />
                        <Chip
                          label={`#${index + 1}`}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 1 }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            )}

            {formData.route_points.length >= 2 && (
              <Grid item xs={12}>
                <Alert severity="info" icon={<TimelineIcon />}>
                  <Typography variant="body2">
                    <strong>Total Trip Mileage:</strong> {calculateTotalMileage().toFixed(2)} km
                    (Start: {formData.route_points[0]?.mileage_reading || 0} km → End: {formData.route_points[formData.route_points.length - 1]?.mileage_reading || 0} km)
                  </Typography>
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={2}
                value={formData.notes}
                onChange={handleChange('notes')}
                placeholder="Additional trip notes"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
          >
            {editingTrip ? 'Update' : 'Create'} Trip
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Trip Details Dialog */}
      <Dialog
        open={openViewDialog}
        onClose={handleCloseView}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Trip Details
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {viewingTrip?.trip_number}
              </Typography>
            </Box>
            <Box>
              <Tooltip title="Print / Save as PDF">
                <IconButton
                  color="primary"
                  onClick={() => printTripDetails(viewingTrip)}
                  disabled={!viewingTrip}
                >
                  <PrintIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={handleCloseView}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {viewLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : !viewingTrip ? (
            <Typography color="textSecondary">No trip selected.</Typography>
          ) : (
            <Grid container spacing={2}>
              {/* Trip Info */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      <TimelineIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      Trip Information
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="textSecondary">Trip Number</Typography>
                        <Typography variant="body1" fontWeight="bold">{viewingTrip.trip_number || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="textSecondary">Status</Typography>
                        <Box mt={0.5}>
                          <Chip label={getStatusLabel(viewingTrip.status)} color={getStatusColor(viewingTrip.status)} size="small" />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="textSecondary">Start</Typography>
                        <Typography variant="body2">{fmtDateTime(viewingTrip.start_date)}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="textSecondary">End</Typography>
                        <Typography variant="body2">{fmtDateTime(viewingTrip.end_date)}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="textSecondary">Duration</Typography>
                        <Typography variant="body2">{getTripDuration(viewingTrip)}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="textSecondary">Total Mileage</Typography>
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          {viewingTrip.total_mileage != null ? `${parseFloat(viewingTrip.total_mileage).toFixed(2)} km` : 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Vehicle & Driver */}
              <Grid item xs={12} sm={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      <VehicleIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      Vehicle
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {viewingTrip.vehicle_number || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {viewingTrip.vehicle_type || 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      <DriverIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      Driver
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {viewingTrip.driver_name || 'N/A'}
                    </Typography>
                    {viewingTrip.driver_phone && (
                      <Typography variant="body2" color="textSecondary">
                        <PhoneIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                        {viewingTrip.driver_phone}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Route */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      <RouteIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      Route
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="textSecondary">Pickup</Typography>
                        <Typography variant="body2">{viewingTrip.pickup_location || 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="textSecondary">Delivery</Typography>
                        <Typography variant="body2">{viewingTrip.delivery_location || 'N/A'}</Typography>
                      </Grid>
                      {viewingTrip.notes && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="textSecondary">Notes</Typography>
                          <Typography variant="body2">{viewingTrip.notes}</Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Route Points */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      <LocationIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      Route Points ({(viewingTrip.route_points || []).length})
                    </Typography>
                    {(viewingTrip.route_points || []).length === 0 ? (
                      <Typography variant="body2" color="textSecondary">
                        No route points recorded.
                      </Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>#</TableCell>
                              <TableCell>Location</TableCell>
                              <TableCell align="right">Odometer</TableCell>
                              <TableCell align="right">Distance</TableCell>
                              <TableCell>Notes</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {[...viewingTrip.route_points]
                              .sort((a, b) => (a.sequence || 0) - (b.sequence || 0))
                              .map((p, i, arr) => {
                                const prev = i > 0 ? arr[i - 1] : null;
                                const delta = prev ? (parseFloat(p.mileage_reading) - parseFloat(prev.mileage_reading)) : null;
                                return (
                                  <TableRow key={p.id || i}>
                                    <TableCell>{i + 1}</TableCell>
                                    <TableCell>{p.location_name || ''}</TableCell>
                                    <TableCell align="right">{parseFloat(p.mileage_reading || 0).toFixed(2)} km</TableCell>
                                    <TableCell align="right">{delta !== null ? `${delta.toFixed(2)} km` : '—'}</TableCell>
                                    <TableCell>{p.notes || '—'}</TableCell>
                                  </TableRow>
                                );
                              })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Record Metadata */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      <ClockIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      Record Info
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="textSecondary">Created At</Typography>
                        <Typography variant="body2">{fmtDateTime(viewingTrip.created_at)}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="textSecondary">Last Updated</Typography>
                        <Typography variant="body2">{fmtDateTime(viewingTrip.updated_at)}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<PrintIcon />}
            onClick={() => printTripDetails(viewingTrip)}
            disabled={!viewingTrip}
          >
            Print
          </Button>
          <Button onClick={handleCloseView} variant="contained">Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TripManagement;